import { type ReactNode } from 'react';

/** Inline markdown: **bold** and `code`. Rendered as React nodes (no HTML injection / XSS). */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const regex = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={`${keyPrefix}-b${i}`}>{m[2]}</strong>);
    else if (m[3] !== undefined)
      nodes.push(
        <code key={`${keyPrefix}-c${i}`} className="rounded bg-bg-base px-1 py-0.5 font-mono text-[0.8em]">{m[3]}</code>,
      );
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/**
 * Minimal, dependency-free markdown renderer for Agent answers. The model emits headings (#..######),
 * bullet/numbered lists, **bold** and `code`; this renders those safely as React elements so the chat
 * shows formatted output instead of raw "###"/"**" syntax. Anything else renders as a paragraph.
 */
export function MarkdownLite({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = (key: string) => {
    if (!list) return;
    const cur = list;
    const items = cur.items.map((it, j) => <li key={`${key}-li${j}`}>{renderInline(it, `${key}-li${j}`)}</li>);
    blocks.push(
      cur.ordered ? (
        <ol key={key} className="ml-5 list-decimal space-y-1">{items}</ol>
      ) : (
        <ul key={key} className="ml-5 list-disc space-y-1">{items}</ul>
      ),
    );
    list = null;
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const key = `l${idx}`;
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (heading) {
      flush(key);
      const lvl = (heading[1] ?? '').length;
      blocks.push(
        <div key={key} className={`font-semibold text-text-primary ${lvl <= 2 ? 'mt-3 text-base' : 'mt-2 text-sm'}`}>
          {renderInline(heading[2] ?? '', key)}
        </div>,
      );
    } else if (bullet) {
      const item = bullet[1] ?? '';
      if (list && !list.ordered) list.items.push(item);
      else {
        flush(key);
        list = { ordered: false, items: [item] };
      }
    } else if (numbered) {
      const item = numbered[1] ?? '';
      if (list && list.ordered) list.items.push(item);
      else {
        flush(key);
        list = { ordered: true, items: [item] };
      }
    } else if (line.trim() === '') {
      flush(key);
    } else {
      flush(key);
      blocks.push(<p key={key} className="whitespace-pre-wrap">{renderInline(line, key)}</p>);
    }
  });
  flush('end');
  return <div className="space-y-1 leading-relaxed">{blocks}</div>;
}
